// =====================================================
// CLINICAL RECORDS TYPES - Sistema de Historias Clínicas
// ConsultAR - Plantillas Dinámicas
// =====================================================

// =====================================================
// PLANTILLAS (TEMPLATES)
// =====================================================

export interface TemplateField {
  id: number;
  template_id: number;
  section_id: number | null;
  field_key: string;                   // Nombre interno del campo (snake_case)
  field_label: string;
  field_type: 'text' | 'textarea' | 'number' | 'date' | 'select' | 'radio' | 'checkbox' | 'file' | 'drawing' | 'textarea_with_attachments';
  placeholder?: string;
  options?: string[] | SelectOption[];  // Para select, radio, checkbox (JSONB)
  default_value?: string;
  is_required: boolean;
  is_visible: boolean;
  validation_rules?: ValidationRules;   // JSONB
  help_text?: string;
  display_order: number;
  width: 'full' | 'half' | 'third';     // Ancho del campo
  depends_on?: DependsOnRule;           // JSONB - Campo del que depende
  created_at: string;
  updated_at: string;
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface DependsOnRule {
  field_key: string;
  value: string | string[];
}

export interface ValidationRules {
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  patternMessage?: string;
}

export interface TemplateSection {
  id: number;
  template_id: number;
  name: string;                       // Nombre/título de la sección
  description?: string;
  display_order: number;
  is_collapsible: boolean;
  is_expanded_default: boolean;       // Si está expandida por defecto
  created_at: string;
  fields: TemplateField[];
}

export interface ClinicalTemplate {
  id: number;
  professional_id: number | null;      // null = plantilla del sistema
  name: string;
  description?: string;
  specialty?: string;                  // Especialidad para la que aplica
  icon: string;                        // Emoji o nombre de icono
  color: string;                       // Color de identificación
  is_active: boolean;
  is_default: boolean;                 // true = plantilla predefinida del sistema
  created_at: string;
  updated_at: string;
  sections?: TemplateSection[];
  usage_count?: number;                // Cantidad de registros con esta plantilla
}

// =====================================================
// REGISTROS CLÍNICOS (RECORDS)
// =====================================================

export interface ClinicalRecord {
  id: number;
  professional_id: number;
  template_id: number;
  patient_id?: number;             // ID si es usuario registrado
  patient_name: string;
  patient_email?: string;
  patient_phone?: string;
  patient_dni?: string;
  patient_birth_date?: string;
  record_date: string;
  form_data?: Record<string, any>;  // Datos del formulario en JSON (opcional porque puede venir como values)
  values?: Record<string, any>;     // Alternativa a form_data que devuelve el backend
  notes?: string;                  // Notas adicionales del profesional
  is_signed?: boolean;              // Si el profesional firmó el registro
  signed_at?: string;
  status?: string;                 // draft, completed, etc.
  version?: number;                 // Para versionado de cambios
  is_deleted?: boolean;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  // Datos de la plantilla (join)
  template_name?: string;
  template_icon?: string;
  template_color?: string;
  // Datos que pueden venir embebidos
  attachments?: RecordAttachment[];
  sections?: TemplateSection[];
}

export interface RecordVersion {
  id: number;
  record_id: number;
  version_number: number;
  form_data?: Record<string, any>;
  changes?: {                        // El backend puede devolver changes en vez de form_data
    values?: Record<string, any>;
    [key: string]: any;
  };
  changed_by: number;
  change_type?: string;              // 'created', 'updated', etc.
  change_reason?: string;
  change_summary?: string;
  created_at: string;
}

export interface RecordAttachment {
  id: number;
  record_id: number;
  file_name: string;
  file_type: string;
  file_url: string;
  file_size: number;
  description?: string;
  category?: string;
  uploaded_by?: number;
  uploaded_at?: string;
  created_at?: string;
}

// =====================================================
// PACIENTES
// =====================================================

export interface PatientSearchResult {
  patient_id?: number;
  patient_name: string;
  patient_email?: string;
  patient_phone?: string;
  patient_dni?: string;
  patient_birth_date?: string;
  patient_identifier: string;      // DNI o email como identificador único
}

export interface PatientHistory {
  patient: PatientSearchResult;
  records: ClinicalRecord[];
  total_records: number;
  last_visit?: string;
  first_visit?: string;
}

// =====================================================
// FORMULARIOS Y UI
// =====================================================

export interface FormState {
  values: Record<string, any>;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  isSubmitting: boolean;
  isDirty: boolean;
}

export interface PatientFormData {
  patient_id?: number;
  patient_name: string;
  patient_email?: string;
  patient_phone?: string;
  patient_dni?: string;
  patient_birth_date?: string;
  is_new_patient: boolean;
}

// =====================================================
// API RESPONSES
// =====================================================

export interface TemplatesResponse {
  success: boolean;
  templates: ClinicalTemplate[];
  count?: number;
}

export interface TemplateDetailResponse {
  success: boolean;
  template: ClinicalTemplate;
}

export interface RecordsResponse {
  success: boolean;
  records: ClinicalRecord[];
  count?: number;
  pagination?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
    offset?: number;
  };
}

export interface RecordDetailResponse {
  success: boolean;
  record: ClinicalRecord;
  attachments?: RecordAttachment[];
  versions?: RecordVersion[];
}

export interface PatientSearchResponse {
  success: boolean;
  patients: PatientSearchResult[];
}

export interface PatientHistoryResponse {
  success: boolean;
  records: ClinicalRecord[];
}

export interface CreateRecordRequest {
  template_id: number;
  patient_id?: number;
  patient_name: string;
  patient_email?: string;
  patient_phone?: string;
  patient_dni?: string;
  patient_birth_date?: string;
  record_date: string;
  form_data: Record<string, any>;
  notes?: string;
}

export interface UpdateRecordRequest {
  form_data?: Record<string, any>;
  notes?: string;
  change_reason?: string;
}

// =====================================================
// CONSTANTES Y UTILIDADES
// =====================================================

export const FIELD_TYPES = {
  text: 'Texto corto',
  textarea: 'Texto largo',
  number: 'Número',
  date: 'Fecha',
  select: 'Lista desplegable',
  radio: 'Opción única',
  checkbox: 'Múltiples opciones',
  file: 'Archivo adjunto',
  drawing: 'Área de dibujo',
  textarea_with_attachments: 'Texto con adjuntos'
} as const;

export const FIELD_WIDTHS = {
  full: '100%',
  half: '50%',
  third: '33.33%'
} as const;

export const DEFAULT_TEMPLATE_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
] as const;

export const DEFAULT_TEMPLATE_ICONS = [
  '📋', '🩺', '💊', '🥄', '👶', '🤰', '❤️', '🧬',
  '🦷', '👁', '🧠', '🦴', '💉', '🔬', '📝', '✨'
] as const;